"use client";

import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { formatFileSize } from "@/lib/marketing/free-template-catalog";

/**
 * The shared "download a file, no sign-up" card on the /free/* pages. Both
 * the filled sample (SamplePdfDownload) and the blank template
 * (BlankTemplateDownload) render through it, so the two blocks look and track
 * the same way.
 *
 * Every link records PostHog `sample_downloaded` with its own payload. A
 * filled sample sends `{ source, document }` (the same shape as the /samples
 * page, app/(marketing)/samples/samples-viewer.tsx); a blank template adds
 * `kind: "blank_template"` and `format`, so the two can be told apart.
 */
export interface DownloadCardLink {
  href: string;
  /** Visible link text. */
  label: string;
  /** File size shown after the label, e.g. "· 104 KB". */
  bytes?: number;
  /** Filled orange button; the others are outlined. */
  primary?: boolean;
  analytics: Readonly<Record<string, string>>;
}

export function DownloadCard({
  heading,
  description,
  note,
  links,
  secondary,
}: {
  heading: string;
  description: string;
  note?: string;
  links: readonly DownloadCardLink[];
  secondary?: { href: string; label: string };
}) {
  const posthog = usePostHog();

  return (
    <div className="rounded-xl border border-border bg-surface p-6 sm:p-8">
      <h3 className="text-lg font-bold text-navy">{heading}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted">{description}</p>
      {note ? <p className="mt-1 text-sm leading-relaxed text-muted">{note}</p> : null}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
        {links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            download
            onClick={() => posthog?.capture?.("sample_downloaded", { ...link.analytics })}
            className={
              link.primary
                ? "inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark"
                : "inline-flex min-h-[44px] items-center justify-center rounded-lg border border-border bg-white px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-accent-light"
            }
          >
            {link.label}
            {link.bytes !== undefined ? (
              <>
                {" "}
                <span className="ml-1 font-normal opacity-80">· {formatFileSize(link.bytes)}</span>
              </>
            ) : null}
          </a>
        ))}
        {secondary ? (
          <Link
            href={secondary.href}
            className="inline-flex min-h-[44px] items-center text-sm font-semibold text-brand-700 hover:underline"
          >
            {secondary.label} &rarr;
          </Link>
        ) : null}
      </div>
    </div>
  );
}
